import { parseStringToDateInMilliseconds } from '@utils/kpi-utils/date';

import { IPortalTaskToLinkDTO } from '@interfaces/portal/dto/taskManagementDTO';
import { IPortalLinkedTask } from '@interfaces/portal/tasks';

import { taskLinkTypesNames } from '@components/ModalComponents/TaskForm/LinkTasksForm/constants';

const taskIsAlreadyLinkedErrorMessage = 'A task with the same link type is already linked to this task';
const finishToStartErrorMessage = 'Linked tasks cannot start until this task finishes. Please adjust the dates to meet the Finish to Start dependency rule.';
const finishToFinishErrorMessage = 'Linked tasks cannot finish until this task finishes. Please adjust the dates to meet the Finish to Finish dependency rule.';
const startToStartErrorMessage = 'Linked tasks cannot start until this task starts. Please adjust the dates to meet the Start to Start dependency rule.';
const startToFinishErrorMessage = 'Linked tasks cannot finish until this task starts. Please adjust the dates to meet the Start to Finish dependency rule.';

const startEndDatesWarningMessage = 'At least one of linked tasks does not have start/end dates. The dependency will be inactive until dates are defined.';
const endDatesWarningMessage = 'At least one of linked tasks does not have end dates. The dependency will be inactive until dates are defined.';
const startDatesWarningMessage = 'At least one of linked tasks does not have start dates. The dependency will be inactive until dates are defined.';

interface IValidateTaskLink {
	linkType: string;
	taskToLink: IPortalTaskToLinkDTO;
	linkedTasks: IPortalLinkedTask[];
	setTaskLinkErrorMessage: (errorMessage: string | null) => void;
	setTaskLinkWarningMessage: (warningMessage: string | null) => void;
	predecessorTaskStartDate?: number | null;
	predecessorTaskEndDate?: number | null;
}

export const validateTaskLink = ({
	linkType,
	taskToLink,
	linkedTasks,
	setTaskLinkErrorMessage,
	setTaskLinkWarningMessage,
	predecessorTaskStartDate,
	predecessorTaskEndDate,
}: IValidateTaskLink) => {
	const isTaskAlreadyLinked = linkedTasks.find(task => task.type.name === linkType && task.id === taskToLink.id);

	if (isTaskAlreadyLinked) {
		setTaskLinkErrorMessage(taskIsAlreadyLinkedErrorMessage);
		setTaskLinkWarningMessage(null);

		return;
	}

	switch (true) {
		case linkType === taskLinkTypesNames.finishToStart: {
			const successorTaskStartDate = taskToLink?.startDate
				&& parseStringToDateInMilliseconds(taskToLink.startDate);

			if (!predecessorTaskEndDate || !successorTaskStartDate) {
				setTaskLinkWarningMessage(startEndDatesWarningMessage);
				setTaskLinkErrorMessage(null);

				return;
			}

			if (predecessorTaskEndDate > successorTaskStartDate) {
				setTaskLinkErrorMessage(finishToStartErrorMessage);
				setTaskLinkWarningMessage(null);

				return;
			}

			setTaskLinkErrorMessage(null);
			setTaskLinkWarningMessage(null);

			break;
		}
		case linkType === taskLinkTypesNames.finishToFinish: {
			const successorTaskEndDate = taskToLink?.endDate
				&& parseStringToDateInMilliseconds(taskToLink.endDate);

			if (!predecessorTaskEndDate || !successorTaskEndDate) {
				setTaskLinkWarningMessage(endDatesWarningMessage);
				setTaskLinkErrorMessage(null);

				return;
			}

			if (predecessorTaskEndDate > successorTaskEndDate) {
				setTaskLinkErrorMessage(finishToFinishErrorMessage);
				setTaskLinkWarningMessage(null);

				return;
			}

			setTaskLinkErrorMessage(null);
			setTaskLinkWarningMessage(null);

			break;
		}
		case linkType === taskLinkTypesNames.startToStart: {
			const successorTaskStartDate = taskToLink?.startDate
				&& parseStringToDateInMilliseconds(taskToLink.startDate);

			if (!predecessorTaskStartDate || !successorTaskStartDate) {
				setTaskLinkWarningMessage(startDatesWarningMessage);
				setTaskLinkErrorMessage(null);

				return;
			}

			if (predecessorTaskStartDate > successorTaskStartDate) {
				setTaskLinkErrorMessage(startToStartErrorMessage);
				setTaskLinkWarningMessage(null);

				return;
			}

			setTaskLinkErrorMessage(null);
			setTaskLinkWarningMessage(null);

			break;
		}
		case linkType === taskLinkTypesNames.startToFinish: {
			const successorTaskEndDate = taskToLink?.endDate
				&& parseStringToDateInMilliseconds(taskToLink.endDate);

			if (!predecessorTaskStartDate || !successorTaskEndDate) {
				setTaskLinkWarningMessage(startEndDatesWarningMessage);
				setTaskLinkErrorMessage(null);

				return;
			}

			if (predecessorTaskStartDate > successorTaskEndDate) {
				setTaskLinkErrorMessage(startToFinishErrorMessage);
				setTaskLinkWarningMessage(null);

				return;
			}

			setTaskLinkErrorMessage(null);
			setTaskLinkWarningMessage(null);

			break;
		}
		default: {
			setTaskLinkErrorMessage(null);
			setTaskLinkWarningMessage(null);
		}
	}
};
